import React, { useState, useEffect } from 'react';
import { MdUpload, MdDownload, MdDelete, MdReceipt, MdCheckCircle } from 'react-icons/md';
import api from '../services/api';
import Sidebar from '../components/Sidebar';

const BRAND = '#068BC9';

export default function AdminCompanyInvoices() {
  const [companies, setCompanies] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    api.get('/api/companies').then(res => {
      setCompanies(res.data);
      if (res.data.length > 0) setSelectedCompanyId(String(res.data[0].id));
    }).catch(() => {});
    fetchInvoices('');
  }, []);

  const fetchInvoices = (companyId) => {
    const url = companyId ? `/api/company-invoices/company/${companyId}` : '/api/company-invoices';
    api.get(url).then(res => setInvoices(res.data)).catch(() => {});
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setExtracted(null);
    setExtracting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(process.env.REACT_APP_API_URL + '/api/company-invoices/extract', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: fd
      });
      const data = await res.json();
      if (data.error) {
        alert('Extraction failed: ' + data.error);
      } else {
        setExtracted(data);
      }
    } catch (e) {
      alert('Failed to extract invoice details');
    } finally {
      setExtracting(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedCompanyId || !file || !extracted) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('companyId', selectedCompanyId);
      fd.append('invoiceNumber', extracted.invoiceNumber || '');
      fd.append('invoiceDate', extracted.invoiceDate ? extracted.invoiceDate.split('/').reverse().join('-') : new Date().toISOString().split('T')[0]);
      fd.append('amount', extracted.invoiceAmount || '0');
      fd.append('serviceType', extracted.description || 'Multiple Services');
      fd.append('description', extracted.description || '');
      fd.append('file', file);
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/company-invoices', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: fd
      });
      if (!response.ok) throw new Error('Upload failed');
      setToast('Invoice uploaded successfully!');
      setShowForm(false);
      setFile(null);
      setExtracted(null);
      fetchInvoices(filterCompany);
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      alert('Upload failed: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await api.delete(`/api/company-invoices/${id}`);
    fetchInvoices(filterCompany);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar onToggle={setSidebarExpanded} />
      <div className="flex-1 overflow-auto transition-all duration-300" style={{ marginLeft: sidebarExpanded ? '240px' : '64px' }}>
        <div className="p-6 space-y-5">
          {toast && <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg text-sm z-50">{toast}</div>}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Company Invoices</h1>
              <p className="text-xs text-gray-400">Upload monthly invoices for clients</p>
            </div>
            <button onClick={() => { setShowForm(!showForm); setExtracted(null); setFile(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: BRAND }}>
              <MdUpload size={16} /> Upload Invoice
            </button>
          </div>

          {showForm && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
              <p className="text-sm font-semibold text-gray-700">New Invoice</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Company <span className="text-red-400">*</span></p>
                  <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none">
                    {companies.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Invoice PDF <span className="text-red-400">*</span></p>
                  <input type="file" accept=".pdf" onChange={handleFileChange} className="text-sm text-gray-600" />
                </div>
              </div>

              {extracting && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  Extracting invoice details...
                </div>
              )}

              {extracted && !extracting && (
                <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                    <MdCheckCircle size={18} /> Invoice details extracted successfully
                  </div>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left px-3 py-2 text-xs text-gray-500 font-medium">Field</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr><td className="px-3 py-2 text-xs text-gray-500">Invoice Number</td><td className="px-3 py-2 text-xs font-medium text-gray-800">{extracted.invoiceNumber || '—'}</td></tr>
                      <tr><td className="px-3 py-2 text-xs text-gray-500">Client Name</td><td className="px-3 py-2 text-xs font-medium text-gray-800">{extracted.clientName || '—'}</td></tr>
                      <tr><td className="px-3 py-2 text-xs text-gray-500">Invoice Date</td><td className="px-3 py-2 text-xs font-medium text-gray-800">{extracted.invoiceDate || '—'}</td></tr>
                      <tr><td className="px-3 py-2 text-xs text-gray-500">Description</td><td className="px-3 py-2 text-xs font-medium text-gray-800">{extracted.description || '—'}</td></tr>
                      <tr><td className="px-3 py-2 text-xs text-gray-500">Invoice Amount</td><td className="px-3 py-2 text-xs font-semibold text-gray-800">₹{parseFloat(extracted.invoiceAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td></tr>
                    </tbody>
                  </table>
                  <div className="flex gap-2 pt-2">
                    <button onClick={handleUpload} disabled={uploading}
                      className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                      style={{ backgroundColor: BRAND }}>
                      {uploading ? 'Uploading...' : 'Confirm & Upload'}
                    </button>
                    <button onClick={() => { setShowForm(false); setFile(null); setExtracted(null); }}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <p className="text-sm font-medium text-gray-600">Filter by Company:</p>
            <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); fetchInvoices(e.target.value); }}
              className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none">
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Invoice List ({invoices.length})</p>
            </div>
            {invoices.length === 0 ? (
              <div className="p-8 text-center">
                <MdReceipt size={36} className="text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No invoices uploaded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>{['Invoice No.','Client','Date','Description','Amount','Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-medium whitespace-nowrap" style={{color:BRAND}}>{inv.invoiceNumber}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{companies.find(c=>c.id==inv.companyId)?.businessName||inv.companyId}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{inv.invoiceDate}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{inv.description||'—'}</td>
                        <td className="px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap">₹{parseFloat(inv.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          <div className="flex gap-2">
                            {inv.filePath && <a href={'https://api.prodiligix.com' + inv.filePath} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 px-2 py-1 rounded text-white text-xs" style={{backgroundColor:BRAND}}>
                              <MdDownload size={12}/> Download</a>}
                            <button onClick={() => handleDelete(inv.id)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-white text-xs bg-red-500">
                              <MdDelete size={12}/> Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
